import { useMemo, type Dispatch, type SetStateAction } from "react";
import { useQuery } from "@tanstack/react-query";
import { Col, Dropdown, Radio, RadioChangeEvent, Row } from "antd";
import { Fragment } from "react/jsx-runtime";
import { journalsQueries } from "@/entities/journal";
import {
  generateAddressTypeName,
  ProjectParticipantInfo,
} from "@/entities/project";
import { useValidateTitle } from "@/features/journal-title-validation";
import { ParticipantsInfiniteScroll } from "@/features/participants-infinite-scroll";
import { BasketIcon, MoreOneIcon } from "@/shared/assets/svg/icons";
import { Can } from "@/shared/lib/casl";
import { useCoordinator } from "@/shared/lib/hooks";
import { fallbackString } from "@/shared/utils";
import { useGetRepresentative } from "../../api";
import {
  useCreateParticipant,
  useDeleteParticipant,
  useTitleAmendmentMode,
} from "../../model";
import { participantTableFactory } from "../../utils/participant-table-factory";
import { CollapsibleSection } from "../collapsible-section";
import { createValidationErrorsGetter } from "../journals-title/model/get-validation-errors-for-section";
import { DeveloperControls, DeveloperRepresentative } from "./ui";

interface IDeveloperCompanyTableNew {
  // errors: FieldErrors<{
  //   journalnumber: string;
  // }>;
  developerTypeLocal: string;
  setDeveloperTypeLocal: Dispatch<SetStateAction<string>>;
}

export const DeveloperCompanyTableNew = ({
  developerTypeLocal,
  setDeveloperTypeLocal,
}: IDeveloperCompanyTableNew) => {
  const { titleId } = useCoordinator();
  const { projectId = "" } = useCoordinator();
  const { data: titleJournal } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );
  const { isLoading } = useQuery(journalsQueries.getTitleJournal({ titleId }));
  const titleStatus =
    titleJournal?.mainJournal?.titleJournal?.status === "NEW" &&
    // @ts-expect-error versionNumber отсутствует в openapi типе TitleJournalViewDto
    !titleJournal?.mainJournal?.titleJournal?.versionNumber;
  const { isNewAmendment } = useTitleAmendmentMode();
  const canAddData = titleStatus || isNewAmendment;

  const { errorList } = useValidateTitle();

  const { representative: developer, isFetching: isDeveloperFetching } =
    useGetRepresentative("developer");

  const validationErrors = useMemo(() => {
    if (!developer?.company) {
      return [];
    }
    return errorList.filter((error) => [6, 15].includes(error.id));
  }, [errorList, developer]);

  const getValidationErrorsForSection = useMemo(
    () => createValidationErrorsGetter(errorList),
    [errorList],
  );

  const { mutate: createParticipant, isPending: createParticipantPending } =
    useCreateParticipant({ titleId });

  const { mutate: deleteParticipant, isPending: deleteParticipantPending } =
    useDeleteParticipant({ titleId });

  const onPartySelect = async (participant: ProjectParticipantInfo) => {
    if (participant) {
      createParticipant({
        participantType: "developer",
        projectParticipantId: participant.projectParticipantId,
        // @ts-expect-error форма participants не совпадает с openapi типом ответа
        developerType: developerTypeLocal,
        companyId: participant.company.companyId,
      });
    }
  };

  const handleDelete = () => {
    deleteParticipant({
      projectParticipantId: developer?.projectParticipantId ?? "",
      participantType: "developer",
      mutateOptions: {
        onSuccess: () => setDeveloperTypeLocal("developer"),
      },
    });
  };

  const handleChangeDeveloperType = (e: RadioChangeEvent) => {
    setDeveloperTypeLocal(e.target.value);
  };

  const menu = [
    {
      key: "delete",
      label: "Удалить",
      icon: <BasketIcon />,
      onClick: handleDelete,
    },
  ];

  const table = participantTableFactory(developer);
  return (
    <CollapsibleSection
      title="Сведения о застройщике, эксплуатирующей организации или региональном операторе"
      spinning={isLoading || deleteParticipantPending || isDeveloperFetching}
      validationErrors={validationErrors}
      sectionId="developer-company"
      getValidationErrorsForSection={getValidationErrorsForSection}
      isEmpty={!developer?.company}
    >
      <>
        {developer?.company ? (
          <div>
            <div className="journal-title-block-header">
              <div className="journal-title-company-name">
                <div>{developer.company?.fullName}</div>
              </div>
              {titleStatus && !isNewAmendment ? (
                <Can I="edit" a="title_journals">
                  <Dropdown menu={{ items: menu }} trigger={["click"]}>
                    <span className="journal-title-menu-trigger">
                      <MoreOneIcon width={24} height={24} />
                    </span>
                  </Dropdown>
                </Can>
              ) : null}
            </div>
            <Row gutter={[16, 16]}>
              {table.map(({ id, title, info }) => (
                <Fragment key={id}>
                  <Col span={8} style={{ color: "#888888" }}>
                    {fallbackString(title)}
                  </Col>
                  <Col span={16} className="table-info-content">
                    {fallbackString(generateAddressTypeName(info))}
                  </Col>
                </Fragment>
              ))}
            </Row>
          </div>
        ) : (
          <>
            {canAddData ? (
              <Can I="edit" a="title_journals">
                <>
                  <div>Тип организации</div>
                  <Radio.Group
                    onChange={handleChangeDeveloperType}
                    value={developerTypeLocal}
                    className="journal-title-type-radio"
                    options={[
                      { value: "developer", label: "Застройщик" },
                      {
                        value: "operating",
                        label: "Эксплуатирующая организация",
                      },
                      { value: "regional", label: "Региональный оператор" },
                    ]}
                  />
                  <div className="journal-title-add-org-block">
                    <div className="journal-title-org-heading">Организация</div>
                    <ParticipantsInfiniteScroll
                      projectId={projectId}
                      onPartySelect={onPartySelect}
                      isLoading={createParticipantPending}
                    />
                  </div>
                </>
              </Can>
            ) : null}
          </>
        )}
      </>
      <DeveloperRepresentative />
      <DeveloperControls />
    </CollapsibleSection>
  );
};
